/**
 * Soft, always-following blob shadows under the characters — not `Decal`s
 * (those are statically placed map content); these track a live position AND
 * facing every frame.
 *
 * Two shapes of the same thing:
 *
 * - `buildPlayerShadow()` returns a free-standing object the caller drives
 *   itself, in the same frame-loop spot that already moves the player mesh
 *   (`playerShadow.rotation.y = snap.player.facing`).
 * - `buildActorShadow()` returns a bare plane meant to be PARENTED to an NPC's
 *   object root. That root is already moved and yawed every frame by
 *   `NpcAnimators.syncActors`, and already hidden by a routine step's "went
 *   inside" — so an NPC shadow needs no driving code at all, and cannot fall
 *   out of step with the body it belongs to. Every NPC gets one, whether it is
 *   a named quest-giver or one of the sixteen ambient citizens: without it a
 *   body reads as hovering a few centimetres off the ground next to a player
 *   who is planted on it.
 *
 * The PLAYER's is returned as a group wrapping the actual plane — same reason
 * as `decal-mesh.ts`: the plane's own local `rotation.x = -PI/2` ("lie flat")
 * and the group's `rotation.y` (yaw, set by the caller) need to compose
 * cleanly. Setting both on a single Object3D's Euler would mix under
 * THREE's fixed intrinsic rotation order instead of being independent
 * axes; wrapping avoids that ambiguity entirely, exactly like the decal fix.
 *
 * Yaw matters here because the shadow is an oval, not a circle (see
 * generation notes below) — its wide axis needs to turn with the character,
 * not stay fixed to the world, or it reads as sliding sideways as the
 * player turns instead of rotating with them.
 *
 * No `alphaTest` (unlike `decal-mesh.ts`'s cutout): the whole point of this
 * texture is a smooth fade to fully transparent at the edge, and a hard
 * cutoff would carve a visible ring where the gradient crosses the
 * threshold. Pure alpha blend + `depthWrite: false` instead.
 */
import * as THREE from 'three';

const SHADOW_URL = '/decals/shadow-circle.webp';
// The texture itself is an oval (drawn as an ellipse, not a stretched
// circle — see the generation notes for why), wider than tall at roughly a
// 1.6:1 ratio. These dimensions match that ratio so the plane doesn't
// re-distort it: SHADOW_WIDTH is side-to-side, SHADOW_DEPTH is front/back
// (both relative to the character's own facing, since this now rotates
// with it).
const SHADOW_WIDTH = 1.3;
const SHADOW_DEPTH = 0.8;
/** Just above the ground (0) and clear of a placed decal's own 0.01 lift, so neither z-fights the other. */
const SHADOW_HEIGHT = 0.015;

/**
 * The geometry, material and texture one BATCH of blob shadows shares.
 *
 * Two dozen NPCs each loading their own copy of a 256-px greyscale blob would
 * be two dozen GPU uploads of the same image. Handed in rather than held in a
 * module-level singleton on purpose: `scene.ts`'s `dispose()` sweep frees the
 * geometry/material/map of every mesh it can reach, so a module-level texture
 * would be disposed on teardown and then handed to the NEXT mount already
 * dead. Scene-owned means the lifetime matches the sweep that ends it.
 *
 * The sweep reaching the same shared objects once per shadow is fine —
 * `dispose()` on an already-disposed texture or material is a no-op.
 */
export interface BlobShadowAssets {
  geometry: THREE.PlaneGeometry;
  material: THREE.MeshBasicMaterial;
}

/** One batch's worth of shared shadow assets. Call once per scene. */
export function createBlobShadowAssets(): BlobShadowAssets {
  const texture = new THREE.TextureLoader().load(SHADOW_URL);
  texture.colorSpace = THREE.SRGBColorSpace;
  return {
    geometry: new THREE.PlaneGeometry(SHADOW_WIDTH, SHADOW_DEPTH),
    material: new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  };
}

/**
 * One NPC's shadow, to be added as a child of that NPC's object root.
 *
 * No `Group` wrapper, unlike the player's: the root IS the wrapper here, and
 * the Euler-mixing problem the player's version avoids does not arise between
 * two separate objects — the root's yaw and this plane's "lie flat" compose as
 * a matrix product, not as one Euler.
 *
 * `raycast` is stubbed out because the object root is a PICKABLE and
 * `Picker.pick` intersects recursively, walking up from whatever it hits to
 * the nearest ancestor carrying an `objectId`. Left alive, this 1.3 x 0.8 m
 * disc of ground would become part of the NPC's click target: hovering the
 * dirt beside someone would highlight them and a click there would open their
 * dialog.
 */
export function buildActorShadow(assets: BlobShadowAssets): THREE.Mesh {
  const plane = new THREE.Mesh(assets.geometry, assets.material);
  plane.name = 'actor-shadow';
  plane.rotation.x = -Math.PI / 2; // lie flat, facing +Y
  plane.position.y = SHADOW_HEIGHT;
  plane.raycast = () => {};
  return plane;
}

export function buildPlayerShadow(): THREE.Object3D {
  const { geometry, material } = createBlobShadowAssets();
  const plane = new THREE.Mesh(geometry, material);
  plane.rotation.x = -Math.PI / 2; // lie flat, facing +Y
  plane.raycast = () => {};

  const group = new THREE.Group();
  group.add(plane);
  group.position.y = SHADOW_HEIGHT;
  return group;
}
